/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { query } from 'lit/decorators/query.js';
import { baseStyles, property, querySlot, querySlotAll, syncProps } from '@cds/core/internal';
import styles from './alert-group.element.scss';
/**
 * Alert groups are containers for a set of alerts. Alert groups can hold one or many alerts
 * inside of them with the expectation that all alerts will be of the same type. The exception
 * to this rule is the `loading` alert type, which will be displayed regardless of the type
 * of alert group containing it.
 *
 * ```typescript
 * import '@cds/core/alert/register.js';
 * ```
 *
 * ```html
 *   <cds-alert-group type="danger">
 *     <cds-alert closable="true">
 *       Single alert
 *       <cds-alert-actions>
 *         buttons, links
 *       </cds-alert-actions>
 *     </cds-alert>
 *     <cds-alert type="loading">
 *       Single Alert
 *     </cds-alert>
 *     <cds-alert closable="true">
 *       Another alert
 *       <cds-alert-actions>
 *         buttons, links
 *       </cds-alert-actions>
 *     </cds-alert>
 *   </cds-alert-group>
 * ```
 *
 * @element cds-alert-group
 * @slot - Content slot for the alerts
 * @cssprop --color
 * @cssprop --icon-color
 * @cssprop --icon-size
 * @cssprop --font-size
 * @cssprop --font-weight
 * @cssprop --letter-spacing
 * @cssprop --padding
 * @cssprop --background
 * @cssprop --border-color
 * @cssprop --border-width
 * @cssprop --border-radius
 */
export class CdsAlertGroup extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Sets the overall height and width of the alerts inside the alert group
         * @type {default | sm}
         */
        this.size = 'default';
        /**
         * Passed down into the alerts inside the alert-group
         * @type {default | banner | light}
         */
        this.type = 'default';
        /**
         * Sets the status of the alerts inside the alert group
         * @type {neutral | info | success | warning | danger | alt | loading}
         */
        this.status = 'neutral';
    }
    render() {
        return html `
      <div
        cds-layout="horizontal wrap:none align:stretch"
        class="${this.pager ? 'private-host' : 'private-host no-pager'}"
      >
        <div class="pager-wrapper">
          <slot name="pager"></slot>
        </div>
        <div class="alert-group-wrapper">
          <div
            class="alerts"
            cds-layout="vertical wrap:none align:horizontal-stretch fill ${this.size === 'sm' ? 'gap:none' : 'gap:xs'}"
          >
            <slot></slot>
          </div>
        </div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.role = 'region';
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.setupAlertsUpdate();
    }
    setupAlertsUpdate() {
        const propsToSync = { status: true, type: true, size: true };
        this.alertSlot?.addEventListener('slotchange', () => this.syncAlerts(propsToSync));
    }
    async syncAlerts(propsToSync) {
        await Promise.all(Array.from(this.alerts).map(a => a.updateComplete));
        this.alerts.forEach(alert => syncProps(alert, this, {
            status: propsToSync.status && this.type !== 'light' && alert.status !== 'loading',
            type: propsToSync.type,
            size: propsToSync.size,
        }));
    }
    updated(props) {
        super.updated(props);
        this.syncAlerts({
            status: props.has('status'),
            type: props.has('type'),
            size: props.has('size'),
        });
    }
    static get styles() {
        return [baseStyles, styles];
    }
}
__decorate([
    property({ type: String })
], CdsAlertGroup.prototype, "size", void 0);
__decorate([
    property({ type: String })
], CdsAlertGroup.prototype, "type", void 0);
__decorate([
    property({ type: String })
], CdsAlertGroup.prototype, "status", void 0);
__decorate([
    querySlotAll('cds-alert')
], CdsAlertGroup.prototype, "alerts", void 0);
__decorate([
    querySlot('.pager, cds-pagination', { assign: 'pager' })
], CdsAlertGroup.prototype, "pager", void 0);
__decorate([
    query('.alerts')
], CdsAlertGroup.prototype, "alertSlot", void 0);
